/**
 * F43-FC §6.4 — extracted from `trainer-verifications/[userId].tsx` (was the
 * inline `ReasonModal`). Shared by the dedicated trainer-verifications page AND
 * the platform-user Trainer panel so a fix lands in one place. Props are
 * byte-identical to the original inline component; `data-testid`s unchanged.
 *
 * Used for: reject profile · reject cert · revoke cert · suspend ·
 * force-deactivate (each a reason-bearing destructive decision).
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useI18n } from '@bravemove/i18n/react';
import { Banner, Button, FormField, Modal, Textarea } from '@bravemove/ui-web';
import { translateApiError } from '../../auth/errors';
import { trainerReasonSchema, type TrainerReasonFormValues } from './reasonSchema';

export function TrainerReasonModal({
  open,
  title,
  description,
  reasonLabel,
  reasonPlaceholder,
  confirmLabel,
  destructive,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly description?: string;
  readonly reasonLabel: string;
  readonly reasonPlaceholder: string;
  readonly confirmLabel: string;
  readonly destructive?: boolean;
  readonly onClose: () => void;
  readonly onSubmit: (reason: string) => Promise<void>;
}) {
  const { t } = useI18n();
  const [serverError, setServerError] = useState<string | null>(null);
  const form = useForm<TrainerReasonFormValues>({
    resolver: zodResolver(trainerReasonSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({ reason: '' });
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setServerError(null);
    }
  }, [open, form]);

  const submit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      await onSubmit(values.reason);
    } catch (err) {
      setServerError(translateApiError(err, t));
    }
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={form.formState.isSubmitting}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant={destructive ? 'destructive' : 'primary'}
            type="button"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
            data-testid="reason-modal-submit"
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {serverError ? (
        <Banner tone="danger" className="mb-4">
          {serverError}
        </Banner>
      ) : null}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="space-y-3"
      >
        <FormField label={reasonLabel} error={form.formState.errors.reason?.message} required>
          <Textarea
            rows={3}
            maxLength={500}
            showCounter
            placeholder={reasonPlaceholder}
            data-testid="reason-modal-input"
            {...form.register('reason')}
          />
        </FormField>
      </form>
    </Modal>
  );
}

export default TrainerReasonModal;
